import type { AppData, CreditCardRecord } from './api'

export function cardBillMonth(
  date: string,
  closingDay: number,
  dueDay = closingDay + 1,
) {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number)
  const actualClosingDay = Math.min(
    closingDay,
    new Date(year, month, 0).getDate(),
  )
  const offset =
    (day >= actualClosingDay ? 1 : 0) + (dueDay <= closingDay ? 1 : 0)
  return addBillMonth(`${year}-${String(month).padStart(2, '0')}`, offset)
}

export function addBillMonth(month: string, offset: number) {
  const [year, value] = month.split('-').map(Number),
    date = new Date(year, value - 1 + offset, 1, 12)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export function cardDueDate(card: CreditCardRecord, month: string) {
  const [year, m] = month.split('-').map(Number),
    last = new Date(year, m, 0).getDate()
  return `${month}-${String(Math.min(card.dueDay, last)).padStart(2, '0')}`
}

export function firstRelevantBillMonth(
  data: AppData,
  card: CreditCardRecord,
  month: string,
) {
  const started = (data.profileStartedAt || data.incomePlan.startDate)?.slice(
    0,
    10,
  )
  return started && cardDueDate(card, month) < started
    ? addBillMonth(month, 1)
    : month
}

function subscriptionOccurrences(
  data: AppData,
  card: CreditCardRecord,
  month: string,
) {
  return data.cardSubscriptions
    .filter(
      (subscription) => subscription.cardId === card.id && subscription.active,
    )
    .flatMap((subscription) => {
      // Registration is not the start of an existing subscription: include its current bill cycle.
      const firstMonth = subscription.createdAt
        ? firstRelevantBillMonth(data, card, subscription.createdAt.slice(0, 7))
        : ''
      return [addBillMonth(month, -2), addBillMonth(month, -1), month]
        .map((candidate) => {
          const [year, value] = candidate.split('-').map(Number),
            last = new Date(year, value, 0).getDate()
          const date = `${candidate}-${String(Math.min(subscription.chargeDay, last)).padStart(2, '0')}`
          return { subscription, date }
        })
        .filter(
          ({ date }) =>
            (!firstMonth || month >= firstMonth) &&
            cardBillMonth(date, card.closingDay, card.dueDay) === month,
        )
    })
}

function installmentOccurrences(
  data: AppData,
  card: CreditCardRecord,
  month: string,
) {
  const target = month.split('-').map(Number)
  return (data.cardInstallments || [])
    .filter((plan) => plan.cardId === card.id && plan.active)
    .flatMap((plan) => {
      const first = plan.firstBillMonth.slice(0, 7).split('-').map(Number)
      const offset = (target[0] - first[0]) * 12 + target[1] - first[1]
      const number = plan.currentInstallment + offset
      if (offset < 0 || number > plan.totalInstallments) return []
      const totalCents = Math.round(Number(plan.totalAmount) * 100)
      const regularCents =
        plan.source === 'new'
          ? Math.floor(totalCents / plan.totalInstallments)
          : Math.round(Number(plan.installmentAmount) * 100)
      const amount =
        (plan.source === 'new' && number === plan.totalInstallments
          ? totalCents - regularCents * (plan.totalInstallments - 1)
          : regularCents) / 100
      return [
        {
          id: `installment-${plan.id}-${month}`,
          planId: plan.id,
          description: `${plan.description} · ${number}/${plan.totalInstallments}`,
          amount,
          kind: 'expense' as const,
          status: 'planned' as const,
          installment: true,
          installmentNumber: number,
          totalInstallments: plan.totalInstallments,
        },
      ]
    })
}

export function cardBill(data: AppData, card: CreditCardRecord, month: string) {
  const started = (data.profileStartedAt || data.incomePlan.startDate)?.slice(
    0,
    10,
  )
  const applicable = !started || cardDueDate(card, month) >= started
  const purchases = data.transactions.filter(
    (transaction) =>
      transaction.paymentMethod === 'credit_card' &&
      transaction.creditCardId === card.id &&
      transaction.status !== 'cancelled' &&
      transaction.transactionDate &&
      cardBillMonth(
        transaction.transactionDate,
        card.closingDay,
        card.dueDay,
      ) === month,
  )
  const subscriptions = subscriptionOccurrences(data, card, month).map(
    ({ subscription, date }) => ({
      id: `sub-${subscription.id}-${date}`,
      description: subscription.description,
      amount: Number(subscription.amount),
      kind: 'expense' as const,
      status: 'planned' as const,
      subscription: true,
      transactionDate: date,
    }),
  )
  const installments = installmentOccurrences(data, card, month)
  const amount = applicable
    ? Math.round(
        [...purchases, ...subscriptions, ...installments].reduce(
          (sum, item) => sum + Number(item.amount),
          0,
        ) * 100,
      ) / 100
    : 0
  const payment = data.transactions.find(
    (transaction) =>
      transaction.creditCardId === card.id &&
      transaction.movementPurpose === 'card_payment' &&
      transaction.status === 'completed' &&
      transaction.transactionDate?.startsWith(month),
  )
  return {
    amount,
    purchases: applicable ? purchases : [],
    subscriptions: applicable ? subscriptions : [],
    installments: applicable ? installments : [],
    paid: !!payment,
    payment,
    applicable,
  }
}

// Estimated bank limit: charged purchases and all remaining installments reserve credit.
// Future subscription charges belong in forecasts, but do not consume today's limit.
export function cardAvailableLimit(
  data: AppData,
  card: CreditCardRecord,
  today: string,
) {
  const months = new Set<string>()
  for (const t of data.transactions)
    if (
      t.creditCardId === card.id &&
      t.transactionDate &&
      t.transactionDate.slice(0, 10) <= today
    )
      months.add(cardBillMonth(t.transactionDate, card.closingDay, card.dueDay))
  for (const plan of data.cardInstallments.filter(
    (p) =>
      p.cardId === card.id &&
      p.active &&
      (!p.purchaseDate || p.purchaseDate <= today),
  ))
    for (let i = 0; i <= plan.totalInstallments - plan.currentInstallment; i++)
      months.add(addBillMonth(plan.firstBillMonth.slice(0, 7), i))
  const earliest = data.cardSubscriptions
    .filter((s) => s.cardId === card.id && s.active)
    .map((s) => s.createdAt?.slice(0, 7) || today.slice(0, 7))
    .sort()[0]
  if (earliest)
    for (
      let m = earliest;
      m <= addBillMonth(today.slice(0, 7), 2);
      m = addBillMonth(m, 1)
    )
      months.add(m)
  let used = 0
  for (const month of months) {
    const bill = cardBill(data, card, month)
    if (bill.paid) continue
    used += bill.purchases
      .filter(
        (t) =>
          t.status === 'completed' && t.transactionDate!.slice(0, 10) <= today,
      )
      .reduce((sum, t) => sum + t.amount, 0)
    used += bill.subscriptions
      .filter((t) => t.transactionDate <= today)
      .reduce((sum, t) => sum + t.amount, 0)
    used += bill.installments
      .filter((t) => {
        const plan = data.cardInstallments.find((p) => p.id === t.planId)!
        return !plan.purchaseDate || plan.purchaseDate <= today
      })
      .reduce((sum, t) => sum + t.amount, 0)
  }
  used = Math.round(used * 100) / 100
  return {
    used,
    available: Math.round((Number(card.creditLimit) - used) * 100) / 100,
  }
}
